import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Users, FileImage, Wallet, Plus, BadgeCheck, Upload, Lock, Eye, ExternalLink, Film, Megaphone, Check, CalendarClock, ShieldAlert, Pencil, Trash2, MessagesSquare } from "lucide-react";
import api, { mediaUrl, formatApiErrorDetail } from "@/lib/api";
import AppNav from "@/components/AppNav";
import CheckoutModal from "@/components/CheckoutModal";
import CreatorCharts from "@/components/CreatorCharts";
import CountUp from "@/components/CountUp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SupporterBadge } from "@/lib/badges";
import { toast } from "sonner";

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [supporters, setSupporters] = useState([]);
  const [showAllTxns, setShowAllTxns] = useState(false);
  const [open, setOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef();
  const [post, setPost] = useState({ caption: "", media_url: "", media_type: "image", is_locked: false, price: 4.99 });
  const [sponsorship, setSponsorship] = useState({ active: false });
  const [sponsorCheckout, setSponsorCheckout] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [cancelSponsorOpen, setCancelSponsorOpen] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [deletePostId, setDeletePostId] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [postSponsor, setPostSponsor] = useState(null); // post being promoted
  const [postSponsorPlan, setPostSponsorPlan] = useState(null);
  const [postSponsorCheckout, setPostSponsorCheckout] = useState(false);
  const [cancelPostSponsorId, setCancelPostSponsorId] = useState(null);
  const [postSponsorStats, setPostSponsorStats] = useState(null);

  const CONTENT_SPONSOR_PLANS = [
    { id: "24h", label: "24 ore", price: 2.99, desc: "Metti in evidenza questo post per 1 giorno" },
    { id: "7d", label: "7 giorni", price: 9.99, desc: "Massima esposizione del post per una settimana" },
  ];

  const SPONSOR_PLANS = [
    { id: "24h", label: "24 ore", price: 4.99, desc: "Boost di visibilità per 1 giorno" },
    { id: "7d", label: "7 giorni", price: 19.99, desc: "Massima esposizione per una settimana" },
  ];

  const MAX_VIDEO = 150 * 1024 * 1024;
  const MAX_IMAGE = 15 * 1024 * 1024;

  const load = async () => {
    const { data } = await api.get("/creator/me");
    setData(data);
    try {
      const { data: sp } = await api.get("/sponsorships/me");
      setSponsorship(sp);
    } catch (e) {}
    api.get("/creator/supporters/me").then(({ data }) => setSupporters(data)).catch(() => setSupporters([]));
  };
  useEffect(() => { load(); }, []);

  const messageFan = async (fanId) => {
    try {
      const { data } = await api.post("/conversations/with-fan", { fan_id: fanId });
      navigate(`/messaggi?c=${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Impossibile aprire la chat");
    }
  };

  const openSponsor = (plan) => { setSelectedPlan(plan); setSponsorCheckout(true); };
  const confirmSponsor = async () => {
    try {
      await api.post("/sponsorships", { plan: selectedPlan.id });
      toast.success("Profilo sponsorizzato!", { description: "Ora appari nella sezione in evidenza del Feed." });
      const { data: sp } = await api.get("/sponsorships/me");
      setSponsorship(sp);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nel pagamento");
      throw e;
    }
  };

  const cancelSponsor = async () => {
    try {
      await api.post("/sponsorships/cancel");
      toast.success("Sponsorizzazione annullata");
      setSponsorship({ active: false });
      setCancelSponsorOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  };

  const sponsorTimeLeft = () => {
    if (!sponsorship.end_date) return "";
    const diff = new Date(sponsorship.end_date).getTime() - Date.now();
    if (diff <= 0) return "scaduta";
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    if (days > 0) return `${days}g ${hours}h`;
    const mins = Math.floor((diff % 3600000) / 60000);
    return `${hours}h ${mins}m`;
  };

  const openPostSponsor = (post, plan) => { setPostSponsor(post); setPostSponsorPlan(plan); setPostSponsorCheckout(true); };

  useEffect(() => {
    if (postSponsor && postSponsor.sponsored) {
      setPostSponsorStats(null);
      api.get(`/posts/${postSponsor.id}/sponsor/stats`).then(({ data }) => setPostSponsorStats(data)).catch(() => setPostSponsorStats(null));
    } else {
      setPostSponsorStats(null);
    }
  }, [postSponsor]);  const confirmPostSponsor = async () => {
    try {
      await api.post(`/posts/${postSponsor.id}/sponsor`, { plan: postSponsorPlan.id });
      toast.success("Contenuto sponsorizzato!", { description: "Ora appare tra i Post in evidenza del Feed." });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nel pagamento");
      throw e;
    }
  };
  const cancelPostSponsor = async () => {
    try {
      await api.post(`/posts/${cancelPostSponsorId}/sponsor/cancel`);
      toast.success("Sponsorizzazione contenuto annullata");
      setCancelPostSponsorId(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  };

  const saveEditPost = async () => {
    setSavingEdit(true);
    try {
      await api.patch(`/posts/${editPost.id}`, {
        caption: editPost.caption,
        is_locked: editPost.is_locked,
        price: editPost.is_locked ? Number(editPost.price) || 0 : 0,
      });
      toast.success("Post aggiornato");
      setEditPost(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nella modifica");
    } finally {
      setSavingEdit(false);
    }
  };

  const doDeletePost = async () => {
    try {
      await api.delete(`/posts/${deletePostId}`);
      toast.success("Post eliminato");
      setDeletePostId(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nell'eliminazione");
    }
  };

  const uploadMedia = async (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    const videoExts = ["mp4", "mov", "webm", "m4v"];
    const imageExts = ["jpg", "jpeg", "png", "gif", "webp"];
    const isVid = videoExts.includes(ext);
    if (!isVid && !imageExts.includes(ext)) {
      toast.error("Formato non supportato. Usa immagini (jpg, png, webp) o video (mp4, mov, webm).");
      return;
    }
    if (isVid && file.size > MAX_VIDEO) {
      toast.error("Video troppo grande. Dimensione massima 150MB.");
      return;
    }
    if (!isVid && file.size > MAX_IMAGE) {
      toast.error("Immagine troppo grande. Dimensione massima 15MB.");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setPost((p) => ({ ...p, media_url: data.url, media_type: data.media_type || (isVid ? "video" : "image") }));
      toast.success(isVid ? "Video caricato" : "Immagine caricata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload fallito");
    } finally {
      setUploading(false);
    }
  };

  const createPost = async () => {
    if (!post.media_url) { toast.error("Carica un'immagine o un video"); return; }
    if (post.is_locked && data?.profile?.verification_status !== "verified") {
      toast.error("Completa la verifica identità per sbloccare la pubblicazione di contenuti a pagamento");
      return;
    }
    setPosting(true);
    try {
      await api.post("/posts", post);
      toast.success("Post pubblicato!");
      setOpen(false);
      setPost({ caption: "", media_url: "", media_type: "image", is_locked: false, price: 4.99 });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally {
      setPosting(false);
    }
  };

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D]"><Loader2 className="w-8 h-8 text-[#E91E8C] animate-spin" /></div>;
  }

  if (!data.profile) {
    return (
      <div className="flex bg-[#0D0D0D] min-h-screen">
        <AppNav />
        <main className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <p className="text-white/60 mb-5">Completa prima il tuo profilo creator.</p>
            <Link to="/onboarding" className="px-6 py-3 rounded-full bg-[#E91E8C] font-semibold" data-testid="go-onboarding">Crea profilo</Link>
          </div>
        </main>
      </div>
    );
  }

  const { profile, posts, earnings, transactions } = data;
  const stats = [
    { icon: Users, label: "Follower", value: profile.follower_count, decimals: 0 },
    { icon: FileImage, label: "Post", value: profile.post_count, decimals: 0 },
    { icon: Wallet, label: "Netto (85%)", value: Number(earnings.net), prefix: "€", decimals: 2, noteValue: earnings.active_subscribers },
  ];

  const withdraw = async () => {
    const b = profile.billing || {};
    const billingIncomplete = !b.iban || !(b.mode === "occasional" ? b.name : b.legal_name);
    if (billingIncomplete) {
      toast("Promemoria: completa i Dati fiscali", {
        description: "Per ricevere i pagamenti aggiungi i tuoi dati fiscali nel profilo. Puoi comunque richiedere il prelievo ora.",
      });
    }
    try {
      const { data } = await api.post("/creator/withdraw");
      toast.success(data.message);
    } catch (e) {
      toast.error("Errore nella richiesta");
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div className="flex bg-[#0D0D0D] min-h-screen">
      <AppNav />
      <main className="flex-1 p-5 sm:p-8 pb-24 md:pb-8 overflow-x-hidden">
        <div className="max-w-4xl">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <img src={mediaUrl(profile.avatar_url)} alt={profile.display_name} className="w-16 h-16 rounded-full object-cover border border-white/10" />
              <div>
                <h1 className="text-2xl font-black font-heading flex items-center gap-2">
                  {profile.display_name}
                  {profile.is_verified && <BadgeCheck className="w-5 h-5 text-[#F5C6D6]" />}
                </h1>
                <p className="text-sm text-white/50">
                  {profile.profile_type === "subscription" ? `Abbonamento €${Number(profile.subscription_price).toFixed(2)}/mese` : "Profilo gratuito"}
                </p>
              </div>
            </div>
            <Link to={`/creator/${profile.id}`} className="text-sm text-[#E91E8C] flex items-center gap-1.5 hover:underline" data-testid="view-public-profile">
              <Eye className="w-4 h-4" /> Vedi profilo pubblico
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8">
            {stats.map((s, i) => (
              <div key={i} data-testid={`stat-${s.label}`} className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-4 sm:p-5">
                <s.icon className="w-6 h-6 text-[#E91E8C] mb-3" />
                <p className="text-xl sm:text-3xl font-black font-heading break-words">
                  <CountUp value={s.value} decimals={s.decimals || 0} prefix={s.prefix || ""} />
                </p>
                <p className="text-[11px] sm:text-xs text-white/50 mt-1">
                  {s.label}
                  {s.noteValue != null && <> · <CountUp value={s.noteValue} /> abbonati</>}
                </p>
              </div>
            ))}
          </div>

          {/* Live analytics charts */}
          <CreatorCharts transactions={transactions} />

          {/* Earnings panel */}
          <div className="bg-[#1A1A1A] border border-white/10 rounded-3xl p-6 mb-8" data-testid="earnings-panel">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-5">
              <h2 className="text-xl font-bold font-heading flex items-center gap-2"><Wallet className="w-5 h-5 text-[#E91E8C]" /> Guadagni</h2>
              <Button onClick={withdraw} data-testid="withdraw-btn" className="rounded-full bg-[#E91E8C] hover:bg-[#D8157B] gap-2 font-semibold">
                Richiedi prelievo
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="flex sm:block items-baseline justify-between bg-[#0D0D0D] rounded-2xl p-4 border border-white/10">
                <p className="text-2xl font-black font-heading" data-testid="earn-gross"><CountUp value={Number(earnings.gross)} decimals={2} prefix="€" /></p>
                <p className="text-xs text-white/50 sm:mt-1">Lordo incassato</p>
              </div>
              <div className="flex sm:block items-baseline justify-between bg-[#0D0D0D] rounded-2xl p-4 border border-white/10">
                <p className="text-2xl font-black font-heading text-white/60" data-testid="earn-commission"><CountUp value={Number(earnings.commission)} decimals={2} prefix="−€" /></p>
                <p className="text-xs text-white/50 sm:mt-1">Commissione {Math.round(earnings.commission_rate * 100)}%</p>
              </div>
              <div className="flex sm:block items-baseline justify-between bg-gradient-to-br from-[#E91E8C]/20 to-[#0D0D0D] rounded-2xl p-4 border border-[#E91E8C]/40">
                <p className="text-2xl font-black font-heading text-[#E91E8C]" data-testid="earn-net"><CountUp value={Number(earnings.net)} decimals={2} prefix="€" /></p>
                <p className="text-xs text-white/60 sm:mt-1">Netto per te (85%)</p>
              </div>
            </div>

            {/* Transactions history */}
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-white/70 mb-3 uppercase tracking-wider">Storico transazioni</h3>
              {(!transactions || transactions.length === 0) ? (
                <p className="text-sm text-white/40 py-4" data-testid="txn-empty">Nessuna transazione ancora.</p>
              ) : (
                <>
                <div className={`rounded-2xl border border-white/10 overflow-hidden ${showAllTxns ? "max-h-[55vh] overflow-y-auto" : ""}`} data-testid="txn-list">
                  <div className="hidden sm:grid grid-cols-4 gap-2 px-4 py-2.5 text-xs uppercase tracking-wider text-white/40 bg-[#0D0D0D] sticky top-0">
                    <span>Data</span><span>Tipo</span><span className="text-right">Lordo</span><span className="text-right">Netto</span>
                  </div>
                  {(showAllTxns ? transactions : transactions.slice(0, 6)).map((t) => {
                    const label = { subscription: "Abbonamento", unlock: "Sblocco", gift: "Regalo", message_ppv: "Contenuto chat" }[t.type] || "Sblocco";
                    return (
                    <div key={t.id} data-testid={`txn-${t.id}`} className="flex sm:grid sm:grid-cols-4 gap-2 px-4 py-3 text-sm items-center border-t border-white/5 first:border-t-0 sm:first:border-t">
                      <span className="flex items-center gap-1.5 flex-1 min-w-0 sm:order-2 sm:flex-none">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${t.type === "subscription" ? "bg-[#E91E8C]" : "bg-[#F5C6D6]"}`} />
                        <span className="flex flex-col sm:block min-w-0">
                          <span className="truncate">{label}</span>
                          <span className="text-xs text-white/40 sm:hidden">{fmtDate(t.created_at)}</span>
                        </span>
                      </span>
                      <span className="hidden sm:block text-white/70 sm:order-1">{fmtDate(t.created_at)}</span>
                      <span className="hidden sm:block text-right text-white/70 sm:order-3">€{Number(t.gross).toFixed(2)}</span>
                      <span className="text-right sm:order-4 shrink-0">
                        <span className="font-medium text-[#E91E8C]">€{Number(t.net).toFixed(2)}</span>
                        <span className="block text-xs text-white/40 sm:hidden">lordo €{Number(t.gross).toFixed(2)}</span>
                      </span>
                    </div>
                    );
                  })}
                </div>
                {transactions.length > 6 && (
                  <button
                    onClick={() => setShowAllTxns((v) => !v)} data-testid="txn-toggle"
                    className="mt-3 w-full text-sm font-semibold text-[#E91E8C] hover:text-[#F5C6D6] transition-colors py-2"
                  >
                    {showAllTxns ? "Mostra meno" : `Mostra tutte (${transactions.length})`}
                  </button>
                )}
                </>
              )}
            </div>
          </div>

          {/* Sponsorship */}
          <div className="bg-[#1A1A1A] border border-white/10 rounded-3xl p-6 mb-8" data-testid="sponsor-panel">
            <div className="flex items-center gap-2 mb-1">
              <Megaphone className="w-5 h-5 text-[#F5C6D6]" />
              <h2 className="text-xl font-bold font-heading">Promuovi il tuo profilo</h2>
            </div>
            <p className="text-sm text-white/50 mb-5">Acquista uno slot di visibilità e appari nella sezione in evidenza del Feed.</p>

            {sponsorship.active ? (
              <div data-testid="sponsor-active">
                <div className="flex items-center gap-3 bg-[#F5C6D6]/10 border border-[#F5C6D6]/30 rounded-2xl p-4">
                  <Check className="w-5 h-5 text-[#F5C6D6] shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#F5C6D6]">Sponsorizzazione attiva</p>
                    <p className="text-xs text-white/50 flex items-center gap-1.5 mt-0.5">
                      <CalendarClock className="w-3.5 h-3.5" /> Scade tra <span className="text-white font-medium" data-testid="sponsor-countdown">{sponsorTimeLeft()}</span> · fino al {fmtDate(sponsorship.end_date)}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {SPONSOR_PLANS.map((plan) => (
                    <Button
                      key={plan.id} onClick={() => openSponsor(plan)} data-testid={`sponsor-extend-${plan.id}`}
                      size="sm" variant="outline" className="rounded-full text-xs gap-1.5 bg-transparent border-[#E91E8C]/40 text-[#E91E8C] hover:bg-[#E91E8C]/10"
                    >
                      <Plus className="w-3.5 h-3.5" /> Estendi {plan.label} · €{plan.price.toFixed(2)}
                    </Button>
                  ))}
                  <Button
                    onClick={() => setCancelSponsorOpen(true)} data-testid="sponsor-cancel-btn"
                    size="sm" variant="ghost" className="rounded-full text-xs text-white/60 hover:text-red-400 hover:bg-red-500/10"
                  >
                    Annulla anticipatamente
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {SPONSOR_PLANS.map((plan) => (
                  <div key={plan.id} data-testid={`sponsor-plan-${plan.id}`} className="bg-[#0D0D0D] border border-white/10 rounded-2xl p-5 flex flex-col">
                    <p className="text-lg font-bold font-heading">{plan.label}</p>
                    <p className="text-xs text-white/50 mt-1 mb-3 flex-1">{plan.desc}</p>
                    <p className="text-2xl font-black font-heading text-[#E91E8C] mb-3">€{plan.price.toFixed(2)}</p>
                    <Button
                      onClick={() => openSponsor(plan)} data-testid={`sponsor-buy-${plan.id}`}
                      className="w-full rounded-full bg-[#E91E8C] hover:bg-[#D8157B] font-semibold gap-2"
                    >
                      <Megaphone className="w-4 h-4" /> Sponsorizza
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Posts */}
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-bold font-heading">I tuoi post</h2>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-post-btn" className="rounded-full bg-[#E91E8C] hover:bg-[#D8157B] gap-2">
                  <Plus className="w-4 h-4" /> Nuovo post
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#1A1A1A] border-white/10 text-white">
                <DialogHeader>
                  <DialogTitle className="font-heading">Crea un nuovo post</DialogTitle>
                  <DialogDescription className="text-white/50">Carica un'immagine o un video e imposta se è gratuito o a pagamento.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div
                    onClick={() => !uploading && fileRef.current.click()}
                    className="aspect-video rounded-2xl border border-dashed border-white/20 flex items-center justify-center cursor-pointer overflow-hidden bg-[#0D0D0D] relative"
                    data-testid="post-media-drop"
                  >
                    {uploading ? (
                      <div className="w-full px-8 text-center" data-testid="upload-progress">
                        <Loader2 className="w-6 h-6 animate-spin text-[#E91E8C] mx-auto mb-3" />
                        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                          <div className="h-full bg-[#E91E8C] transition-all duration-200" style={{ width: `${progress}%` }} />
                        </div>
                        <p className="text-xs text-white/50 mt-2">Caricamento… {progress}%</p>
                      </div>
                    ) : post.media_url ? (
                      post.media_type === "video" ? (
                        <video controls playsInline className="w-full h-full object-contain bg-black" data-testid="post-video-preview">
                          <source src={mediaUrl(post.media_url)} type="video/mp4" />
                          {post.media_url_alt && <source src={mediaUrl(post.media_url_alt)} type="video/webm" />}
                        </video>
                      ) : (
                        <img src={mediaUrl(post.media_url)} alt="preview" className="w-full h-full object-cover" />
                      )
                    ) : (
                      <div className="text-center text-white/40">
                        <div className="flex items-center justify-center gap-2 mb-2"><Upload className="w-6 h-6" /><Film className="w-6 h-6" /></div>
                        Carica immagine o video
                        <p className="text-[11px] text-white/30 mt-1">Max 15MB immagini · 150MB video</p>
                      </div>
                    )}
                    <input ref={fileRef} type="file" accept="image/*,video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm" hidden onChange={(e) => e.target.files[0] && uploadMedia(e.target.files[0])} />
                  </div>
                  <div>
                    <Label className="text-white/80">Didascalia</Label>
                    <Textarea
                      value={post.caption} data-testid="post-caption" rows={2}
                      onChange={(e) => setPost({ ...post, caption: e.target.value })}
                      className="mt-2 bg-[#0D0D0D] border-white/10 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                    />
                  </div>
                  <div className="flex items-center justify-between bg-[#0D0D0D] rounded-xl p-4 border border-white/10">
                    <p className="font-medium flex items-center gap-2"><Lock className="w-4 h-4 text-[#E91E8C]" /> Contenuto a pagamento</p>
                    <Switch checked={post.is_locked} data-testid="post-locked-toggle" onCheckedChange={(v) => setPost({ ...post, is_locked: v })} className="data-[state=checked]:bg-[#E91E8C]" />
                  </div>
                  {post.is_locked && profile.verification_status !== "verified" && (
                    <div data-testid="paid-post-verif-warning" className="flex items-start gap-2.5 bg-amber-400/10 border border-amber-400/30 rounded-xl p-3">
                      <ShieldAlert className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
                      <p className="text-xs text-white/70">Completa la verifica identità per sbloccare la pubblicazione di contenuti a pagamento. Nel frattempo puoi pubblicare contenuti gratuiti.</p>
                    </div>
                  )}
                  {post.is_locked && (
                    <div>
                      <Label className="text-white/80">Prezzo di sblocco (€)</Label>
                      <Input
                        type="number" step="0.01" min="0" value={post.price} data-testid="post-price"
                        onChange={(e) => setPost({ ...post, price: parseFloat(e.target.value) || 0 })}
                        className="mt-2 bg-[#0D0D0D] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C] max-w-[160px]"
                      />
                    </div>
                  )}
                  <Button onClick={createPost} disabled={posting || (post.is_locked && profile.verification_status !== "verified")} data-testid="publish-post" className="w-full h-12 rounded-xl bg-[#E91E8C] hover:bg-[#D8157B] font-semibold disabled:opacity-50">
                    {posting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Pubblica"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {posts.length === 0 ? (
            <div className="text-center py-16 text-white/50 bg-[#1A1A1A] border border-white/10 rounded-3xl" data-testid="dash-empty">Nessun post ancora. Crea il tuo primo contenuto!</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="dash-posts">
              {posts.map((p) => (
                <div key={p.id} className="relative aspect-square rounded-2xl overflow-hidden border border-white/10 group bg-black" data-testid={`dash-post-${p.id}`}>
                  {p.media_type === "video" ? (
                    <video src={mediaUrl(p.media_url)} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                  ) : (
                    <img src={mediaUrl(p.media_url)} alt={p.caption} className="w-full h-full object-cover" />
                  )}
                  <div className="absolute top-2 left-2 flex gap-1.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200 max-md:opacity-100">
                    <button
                      onClick={() => setEditPost({ id: p.id, caption: p.caption, is_locked: p.is_locked, price: p.price || 0 })}
                      data-testid={`edit-post-${p.id}`}
                      className="w-8 h-8 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-white hover:bg-[#E91E8C] transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setPostSponsor(p)}
                      data-testid={`sponsor-post-${p.id}`}
                      className="w-8 h-8 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-white hover:bg-[#F5C6D6] hover:text-black transition-colors"
                    >
                      <Megaphone className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setDeletePostId(p.id)}
                      data-testid={`delete-post-${p.id}`}
                      className="w-8 h-8 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-white hover:bg-red-500 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="absolute bottom-2 right-2 z-10 flex gap-2">
                    {p.sponsored && (
                      <div className="px-2 h-8 rounded-full bg-[#F5C6D6] text-black flex items-center gap-1" data-testid={`dash-post-sponsored-${p.id}`}>
                        <Megaphone className="w-3.5 h-3.5" />
                        <span className="text-[9px] font-bold uppercase tracking-wider">ADV</span>
                      </div>
                    )}
                    {p.media_type === "video" && (
                      <div className="w-8 h-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center">
                        <Film className="w-4 h-4" />
                      </div>
                    )}
                    {p.is_locked && (
                      <div className="w-8 h-8 rounded-full bg-[#E91E8C] flex items-center justify-center">
                        <Lock className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-3 pr-16 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-xs text-white/90 truncate">{p.caption}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* I tuoi supporter */}
          {supporters.length > 0 && (
            <div className="mt-10" data-testid="dash-supporters">
              <h2 className="text-xl font-bold font-heading mb-1 flex items-center gap-2"><Users className="w-5 h-5 text-[#F5C6D6]" /> I tuoi supporter</h2>
              <p className="text-white/50 text-sm mb-4">I fan che ti sostengono. Scrivi loro un messaggio.</p>
              <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl divide-y divide-white/5">
                {supporters.map((s, i) => (
                  <div key={s.fan_id} data-testid={`dash-supporter-${s.fan_id}`} className="flex items-center gap-3 px-4 py-3">
                    <span className="w-5 text-center text-sm font-black font-heading text-white/40">{i + 1}</span>
                    <div className="w-9 h-9 rounded-full bg-[#E91E8C]/15 flex items-center justify-center font-bold text-sm shrink-0">{(s.display_name || "?")[0].toUpperCase()}</div>
                    <span className="flex-1 min-w-0 text-sm font-semibold truncate">{s.display_name}</span>
                    {s.badge && <SupporterBadge badge={s.badge} size="xs" />}
                    <Button onClick={() => messageFan(s.fan_id)} data-testid={`message-fan-${s.fan_id}`} size="sm" variant="outline" className="rounded-full bg-transparent border-white/15 hover:bg-white/5 gap-1.5 shrink-0 h-8">
                      <MessagesSquare className="w-3.5 h-3.5" /> Messaggia
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <CheckoutModal
          open={sponsorCheckout}
          onOpenChange={setSponsorCheckout}
          title="Sponsorizza il tuo profilo"
          subtitle={selectedPlan ? `Slot di visibilità · ${selectedPlan.label}` : ""}
          lines={selectedPlan ? [
            { label: "Piano", value: selectedPlan.label },
            { label: "Visibilità", value: "Sezione in evidenza del Feed" },
          ] : []}
          total={selectedPlan?.price || 0}
          confirmLabel="Simula pagamento riuscito"
          onConfirm={confirmSponsor}
        />

        {/* Content sponsorship: plan selection */}
        <Dialog open={!!postSponsor && !postSponsorCheckout} onOpenChange={(o) => { if (!o) setPostSponsor(null); }}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white sm:max-w-lg" data-testid="post-sponsor-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2"><Megaphone className="w-5 h-5 text-[#F5C6D6]" /> Sponsorizza questo contenuto</DialogTitle>
              <DialogDescription className="text-white/50">Metti in evidenza il tuo post nel Feed: apparirà nella sezione "Post in evidenza" e tra i contenuti degli altri creator.</DialogDescription>
            </DialogHeader>
            {postSponsor?.sponsored ? (
              <div data-testid="post-sponsor-active">
                <div className="flex items-center gap-3 bg-[#0D0D0D] border border-[#F5C6D6]/30 rounded-2xl px-4 py-3 mb-4">
                  <Check className="w-5 h-5 text-[#F5C6D6]" />
                  <div>
                    <p className="text-sm font-semibold text-[#F5C6D6]">Contenuto in evidenza</p>
                    {postSponsor.sponsor_end_date && (
                      <p className="text-xs text-white/50 flex items-center gap-1.5 mt-0.5">
                        <CalendarClock className="w-3.5 h-3.5" /> Attivo fino al {fmtDate(postSponsor.sponsor_end_date)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4" data-testid="post-sponsor-stats">
                  <div className="bg-[#0D0D0D] border border-white/10 rounded-2xl p-4 text-center">
                    <p className="text-2xl font-black font-heading text-white" data-testid="sponsor-impressions">{postSponsorStats ? postSponsorStats.impressions : "—"}</p>
                    <p className="text-[11px] text-white/50 uppercase tracking-wide mt-0.5">Visualizzazioni</p>
                  </div>
                  <div className="bg-[#0D0D0D] border border-white/10 rounded-2xl p-4 text-center">
                    <p className="text-2xl font-black font-heading text-[#E91E8C]" data-testid="sponsor-unlocks">{postSponsorStats ? postSponsorStats.unlocks : "—"}</p>
                    <p className="text-[11px] text-white/50 uppercase tracking-wide mt-0.5">Sblocchi</p>
                  </div>
                </div>
                <p className="text-xs text-white/50 mb-3">Estendi la durata:</p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {CONTENT_SPONSOR_PLANS.map((plan) => (
                    <button
                      key={plan.id} onClick={() => openPostSponsor(postSponsor, plan)} data-testid={`post-sponsor-extend-${plan.id}`}
                      className="bg-[#0D0D0D] border border-white/10 rounded-2xl p-4 text-left hover:border-[#F5C6D6]/50 transition-colors"
                    >
                      <p className="font-bold font-heading">{plan.label}</p>
                      <p className="text-[#E91E8C] font-semibold text-sm mt-1">€{plan.price.toFixed(2)}</p>
                    </button>
                  ))}
                </div>
                <Button
                  variant="outline" onClick={() => { setCancelPostSponsorId(postSponsor.id); setPostSponsor(null); }}
                  data-testid="post-sponsor-cancel-btn"
                  className="w-full bg-transparent border-red-500/40 text-red-400 hover:bg-red-500/10"
                >
                  Annulla sponsorizzazione
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {CONTENT_SPONSOR_PLANS.map((plan) => (
                  <div key={plan.id} data-testid={`post-sponsor-plan-${plan.id}`} className="bg-[#0D0D0D] border border-white/10 rounded-2xl p-5 flex flex-col">
                    <p className="font-bold font-heading text-lg">{plan.label}</p>
                    <p className="text-2xl font-black font-heading text-[#E91E8C] my-1">€{plan.price.toFixed(2)}</p>
                    <p className="text-xs text-white/50 flex-1 mb-4">{plan.desc}</p>
                    <Button
                      onClick={() => openPostSponsor(postSponsor, plan)} data-testid={`post-sponsor-buy-${plan.id}`}
                      className="w-full bg-[#E91E8C] hover:bg-[#D8157B] text-white gap-2"
                    >
                      <Megaphone className="w-4 h-4" /> Sponsorizza
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Content sponsorship: checkout */}
        <CheckoutModal
          open={postSponsorCheckout}
          onOpenChange={(o) => { setPostSponsorCheckout(o); if (!o) setPostSponsor(null); }}
          title="Sponsorizza il contenuto"
          subtitle={postSponsorPlan ? `Post in evidenza · ${postSponsorPlan.label}` : ""}
          lines={postSponsorPlan ? [
            { label: "Piano", value: postSponsorPlan.label },
            { label: "Visibilità", value: "Sezione Post in evidenza del Feed" },
          ] : []}
          total={postSponsorPlan?.price || 0}
          confirmLabel="Simula pagamento riuscito"
          onConfirm={confirmPostSponsor}
        />

        {/* Content sponsorship: cancel confirm */}
        <AlertDialog open={!!cancelPostSponsorId} onOpenChange={(o) => { if (!o) setCancelPostSponsorId(null); }}>
          <AlertDialogContent className="bg-[#1A1A1A] border-white/10 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">Annullare la sponsorizzazione del contenuto?</AlertDialogTitle>
              <AlertDialogDescription className="text-white/50">
                Il post verrà rimosso subito dalla sezione "Post in evidenza". L'importo già pagato non è rimborsabile.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-white/10 hover:bg-white/5 text-white">Mantieni attiva</AlertDialogCancel>
              <AlertDialogAction onClick={cancelPostSponsor} data-testid="post-sponsor-cancel-confirm" className="bg-red-500 hover:bg-red-600 text-white">Annulla sponsorizzazione</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>


        {/* Cancel sponsorship confirm */}
        <AlertDialog open={cancelSponsorOpen} onOpenChange={setCancelSponsorOpen}>
          <AlertDialogContent className="bg-[#1A1A1A] border-white/10 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">Annullare la sponsorizzazione?</AlertDialogTitle>
              <AlertDialogDescription className="text-white/50">
                Il tuo profilo verrà rimosso subito dalla sezione in evidenza. L'importo già pagato non è rimborsabile.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-white/10 hover:bg-white/5 text-white">Mantieni attiva</AlertDialogCancel>
              <AlertDialogAction onClick={cancelSponsor} data-testid="sponsor-cancel-confirm" className="bg-red-500 hover:bg-red-600 text-white">Annulla sponsorizzazione</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit post */}
        <Dialog open={!!editPost} onOpenChange={(o) => { if (!o) setEditPost(null); }}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-heading">Modifica post</DialogTitle>
              <DialogDescription className="text-white/50">Aggiorna didascalia, prezzo e stato del contenuto.</DialogDescription>
            </DialogHeader>
            {editPost && (
              <div className="space-y-4">
                <div>
                  <Label className="text-white/80">Didascalia</Label>
                  <Textarea
                    value={editPost.caption} data-testid="edit-post-caption"
                    onChange={(e) => setEditPost({ ...editPost, caption: e.target.value })}
                    className="mt-2 bg-[#0D0D0D] border-white/10 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                  />
                </div>
                <div className="flex items-center justify-between bg-[#0D0D0D] border border-white/10 rounded-xl px-4 py-3">
                  <div>
                    <Label className="text-white/80">Contenuto a pagamento</Label>
                    <p className="text-xs text-white/40 mt-0.5">Attiva per bloccare il contenuto</p>
                  </div>
                  <Switch
                    checked={editPost.is_locked} data-testid="edit-post-locked-toggle"
                    onCheckedChange={(v) => setEditPost({ ...editPost, is_locked: v })}
                    className="data-[state=checked]:bg-[#E91E8C]"
                  />
                </div>
                {editPost.is_locked && data?.profile?.verification_status !== "verified" && (
                  <div className="flex items-start gap-2.5 bg-amber-400/10 border border-amber-400/30 rounded-xl p-3">
                    <ShieldAlert className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
                    <p className="text-xs text-white/70">Completa la verifica identità per rendere questo contenuto a pagamento.</p>
                  </div>
                )}
                {editPost.is_locked && (
                  <div>
                    <Label className="text-white/80">Prezzo di sblocco (€)</Label>
                    <Input
                      type="number" step="0.01" min="0" value={editPost.price} data-testid="edit-post-price"
                      onChange={(e) => setEditPost({ ...editPost, price: e.target.value })}
                      className="mt-2 bg-[#0D0D0D] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C] max-w-[160px]"
                    />
                  </div>
                )}
                <Button
                  onClick={saveEditPost}
                  disabled={savingEdit || (editPost.is_locked && data?.profile?.verification_status !== "verified")}
                  data-testid="edit-post-save"
                  className="w-full h-12 rounded-xl bg-[#E91E8C] hover:bg-[#D8157B] font-semibold disabled:opacity-50"
                >
                  {savingEdit ? <Loader2 className="w-5 h-5 animate-spin" /> : "Salva modifiche"}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete post confirm */}
        <AlertDialog open={!!deletePostId} onOpenChange={(o) => { if (!o) setDeletePostId(null); }}>
          <AlertDialogContent className="bg-[#1A1A1A] border-white/10 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">Eliminare questo post?</AlertDialogTitle>
              <AlertDialogDescription className="text-white/50">
                Questa azione è definitiva: il contenuto verrà rimosso per sempre e non sarà più visibile ai fan.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-white/10 hover:bg-white/5 text-white">Annulla</AlertDialogCancel>
              <AlertDialogAction onClick={doDeletePost} data-testid="delete-post-confirm" className="bg-red-500 hover:bg-red-600 text-white">Elimina definitivamente</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}
